import { Box } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '~/client/components/base/button';
import { DesignToken } from '~/client/components/base/core';
import { CircularProgress } from '~/client/components/base/progress';
import Spacer, { spc } from '~/client/components/base/spacer';
import Text from '~/client/components/base/text';
import { useOrganizationProjectContext } from '~/client/components/organization-context';
import { usePermissions } from '~/client/contexts/permissions-context';
import { aiDashboardUtils } from '~/client/routes';

const CenteredContainer = muiStyled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 'calc(100vh - 100px)',
  padding: 24,
  maxWidth: 600,
  margin: '0 auto',
  textAlign: 'center',
}));

const BrandedButton = muiStyled(Button)(
  () => css`
    .MuiButton-root.MuiButton-contained {
      background-color: var(
        --org-background-color,
        ${DesignToken.brand.primary}
      );
      color: white;
      &:hover {
        background-color: var(
          --org-background-color,
          ${DesignToken.brand.primary}
        );
        opacity: 0.9;
      }
    }
  `
);

interface Props {
  className?: string;
}

const DashboardOrganization: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const { navigateTo: navigateToAiSystem } = aiDashboardUtils.useHelper();
  const { hasCapability } = usePermissions();

  // Use centralized organization project context instead of separate query
  const { organizationProjectId, loading: isLoading } =
    useOrganizationProjectContext();

  // Check if user can set up an org compliance project
  const isOrgAdmin = hasCapability('org:create_project');

  // Redirect to org project dashboard if it exists
  useEffect(() => {
    if (!isLoading && organizationProjectId) {
      navigateToAiSystem('DASHBOARD', {
        params: { projectId: organizationProjectId },
      });
    }
  }, [isLoading, organizationProjectId, navigateToAiSystem]);

  const handleSetupOrganization = () => {
    navigate('/dashboard/create-ai-system', {
      state: { source: 'organization' },
    });
  };

  // Show loading while fetching profile or org project
  if (isLoading) {
    return (
      <CenteredContainer className={className}>
        <CircularProgress />
      </CenteredContainer>
    );
  }

  // If org project exists, don't render anything (redirect will happen)
  if (organizationProjectId) {
    return null;
  }

  // Show setup button only for org admins, otherwise show message
  return (
    <CenteredContainer className={className}>
      <Text sz="h1" color={DesignToken.nav.itemText}>
        Organization Compliance Setup
      </Text>
      <Spacer mb={spc.m} />
      <Text sz="body16" color={DesignToken.textMuted}>
        {isOrgAdmin
          ? 'Set up your organization to run high-level governance assessments. This helps you evaluate your overall AI readiness, maturity, risks, and compliance across your entire organization.'
          : 'Your organization has not set up a compliance project yet. Please contact your organization administrator to set up the organization compliance project.'}
      </Text>
      {isOrgAdmin && (
        <>
          <Spacer mb={spc.xl} />
          <BrandedButton
            onClick={handleSetupOrganization}
            size="large"
            aria-label="Set up your organization's AI governance system"
          >
            Setup Your Organization Compliance Project
          </BrandedButton>
        </>
      )}
    </CenteredContainer>
  );
};

export default DashboardOrganization;
